/**
 * OfflineQueue.js
 * Modeled after Stocky's POS offline sales queue (pos_offline_sales_v1).
 * Safely enqueues offline orders, invoices, and payment completions,
 * provides concurrency guards during sync, and synthesizes local printable receipts.
 */

import { readJSON, writeJSON, POS_OFFLINE_ORDERS_KEY, OfflineStorage } from "./OfflineStorage";

const generateOfflineId = (prefix = "off") => {
  try {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 6)}`;
  } catch (e) {
    return `${prefix}-${Date.now()}`;
  }
};

const notifyQueueChanged = (queue) => {
  if (typeof window === "undefined") return;
  try {
    const pendingCount = (queue || []).filter(
      (o) => o && (o.status === "pending" || o.status === "syncing")
    ).length;
    window.dispatchEvent(
      new CustomEvent("restro_offline_queue_changed", {
        detail: { pendingCount, totalCount: (queue || []).length }
      })
    );
  } catch (e) {}
};

export const OfflineQueue = {
  /**
   * Get all queued offline orders
   */
  getOfflineOrders() {
    const list = readJSON(POS_OFFLINE_ORDERS_KEY, []);
    return Array.isArray(list) ? list : [];
  },

  /**
   * Get count of pending orders needing synchronization
   */
  getPendingCount() {
    const list = this.getOfflineOrders();
    return list.filter((o) => o && (o.status === "pending" || o.status === "syncing")).length;
  },

  /**
   * Enqueue an order created or completed while offline
   * @param {'ORDER'|'ORDER_AND_INVOICE'|'PAY_AND_COMPLETE'} actionType
   * @param {Object} payload - The arguments passed to createOrder / createOrderAndInvoice
   */
  enqueueOfflineOrder(actionType, payload) {
    const queue = this.getOfflineOrders();
    const now = new Date().toISOString();
    const offlineId = generateOfflineId("ord");

    const record = {
      id: offlineId,
      actionType, // 'ORDER' | 'ORDER_AND_INVOICE' | 'PAY_AND_COMPLETE'
      transaction_uuid: offlineId,
      createdAt: now,
      updatedAt: now,
      status: "pending", // 'pending' | 'syncing' | 'synced' | 'failed'
      attempts: 0,
      lastError: null,
      payload: {
        ...payload,
        offline_id: offlineId,
        transaction_uuid: offlineId,
        created_at_client: now
      }
    };

    queue.push(record);
    writeJSON(POS_OFFLINE_ORDERS_KEY, queue);
    notifyQueueChanged(queue);
    return record;
  },

  /**
   * Update a specific order in the queue
   */
  _updateOrder(id, updater) {
    if (!id) return;
    const queue = this.getOfflineOrders();
    let changed = false;
    const next = queue.map((item) => {
      if (!item || item.id !== id) return item;
      const updated = typeof updater === "function" ? updater({ ...item }) : item;
      changed = true;
      return {
        ...item,
        ...updated,
        updatedAt: new Date().toISOString()
      };
    });
    if (changed) {
      writeJSON(POS_OFFLINE_ORDERS_KEY, next);
      notifyQueueChanged(next);
    }
  },

  markOrderAsSyncing(id) {
    this._updateOrder(id, (o) => ({
      status: "syncing",
      attempts: (o.attempts || 0) + 1,
      lastError: null
    }));
  },

  markOrderAsSynced(id, remoteResponse = null) {
    this._updateOrder(id, (o) => ({
      status: "synced",
      remoteResponse,
      syncedAt: new Date().toISOString(),
      lastError: null
    }));
  },

  markOrderAsFailed(id, errorMessage, statusCode = null) {
    this._updateOrder(id, () => ({
      status: "failed",
      lastError: {
        message: errorMessage || "Unknown sync error",
        statusCode
      }
    }));
  },

  pruneSyncedOrders() {
    const queue = this.getOfflineOrders();
    const next = queue.filter((o) => !o || o.status !== "synced");
    writeJSON(POS_OFFLINE_ORDERS_KEY, next);
    notifyQueueChanged(next);
  },

  /**
   * Synthesize a local printable invoice and receipt payload directly from offline cart state
   * so receipts can print immediately (<5ms) without a cloud server roundtrip.
   */
  synthesizeReceiptData(cart, orderSummary, paymentDetails = {}, auxiliary = {}) {
    const catalog = OfflineStorage.getCachedBootstrap() || {};
    const storeSettings = catalog.storeSettings || {};
    const printSettings = catalog.printSettings || {};
    const tables = catalog.storeTables || [];
    const rooms = catalog.rooms || [];

    const now = new Date();
    const formattedDate = now.toLocaleString();
    const tableObj = tables.find((t) => String(t.id) === String(auxiliary.tableId));
    const roomObj = rooms.find((r) => String(r.id) === String(auxiliary.roomId) || r.name === auxiliary.roomName);

    const items = (cart || []).map((c) => ({
      item_id: c.id,
      title: c.title,
      quantity: c.quantity,
      price: c.price,
      variant_title: c.variant?.title || null,
      addons: c.addons || [],
      notes: c.notes || ""
    }));

    return {
      isOfflineReceipt: true,
      offlineId: paymentDetails.offlineId || generateOfflineId("inv"),
      invoiceNumber: `OFF-${now.getTime().toString().slice(-6)}`,
      date: formattedDate,
      store: {
        name: storeSettings.store_name || "Restaurant POS",
        address: storeSettings.address || "",
        phone: storeSettings.phone || "",
        taxNumber: storeSettings.vat_number || storeSettings.pan_number || "",
        currency: storeSettings.currency || "NPR"
      },
      table: tableObj ? tableObj.table_title : auxiliary.tableTitle || "",
      room: roomObj ? roomObj.name : auxiliary.roomName || "",
      deliveryType: auxiliary.deliveryType || "dinein",
      items,
      subTotal: orderSummary.subTotal || orderSummary.itemsTotal || 0,
      discount: orderSummary.discount || 0,
      taxTotal: orderSummary.taxTotal || 0,
      serviceChargeTotal: orderSummary.serviceChargeTotal || 0,
      roundOff: orderSummary.roundOff || 0,
      total: orderSummary.payableTotal || orderSummary.total || 0,
      paidAmount: paymentDetails.paidAmount || orderSummary.payableTotal || 0,
      paymentMethod: paymentDetails.paymentMethodTitle || "Cash",
      printSettings
    };
  }
};

export default OfflineQueue;
